import { Badge, Box, Group, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import ResourceIcon from '@features/Resource/ResourceIcon';

import { ResourceType } from '@app-types/blackroom-api';

import { CellProps, ResourceCellInfos, TableMeta } from '../types';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import Highlight from '@app/shared/ui-kit/atoms/Highlight';

function CellResource<T extends ResourceCellInfos>({
  row,
  table,
  globalFilter,
  ...props
}: CellProps<T>) {
  const { t } = useTranslation();
  const resource = row.original;

  const context = (table.options?.meta as TableMeta | undefined)?.context;
  const dataroomFolderId = context?.dataroomFolderId;
  const datadumpFolderId = context?.datadumpFolderId;

  const systemFolderType =
    resource.type === ResourceType.Folder
      ? resource.id === dataroomFolderId
        ? ('dataroom' as const)
        : resource.id === datadumpFolderId
          ? ('dump' as const)
          : undefined
      : undefined;

  const isFolderExpanded =
    resource.type === ResourceType.Folder && row.getIsExpanded();

  return (
    <Group
      wrap="nowrap"
      gap="xs"
      style={{
        paddingLeft: `${row.depth * 20}px`,
      }}
    >
      <Box>
        <ResourceIcon
          resource={resource}
          folderExpanded={isFolderExpanded}
          systemFolderType={systemFolderType}
          withColor
        />
      </Box>
      <Tooltip label={resource.name} openDelay={500}>
        <div style={{ overflow: 'hidden' }}>
          <Highlight
            size="sm"
            highlight={globalFilter ?? ''}
            truncate
            fw={FONT_WEIGHT.bold}
          >
            {resource.name}
          </Highlight>
        </div>
      </Tooltip>
      {systemFolderType === 'dump' && (
        <Badge size="xs" variant="light" color="blue" style={{ flexShrink: 0 }}>
          {t('Resource:Common.resource.datadump')}
        </Badge>
      )}
      {systemFolderType === 'dataroom' && (
        <Badge
          size="xs"
          variant="light"
          color="violet"
          style={{ flexShrink: 0 }}
        >
          {t('Resource:Common.resource.dataroom')}
        </Badge>
      )}
    </Group>
  );
}

export default CellResource;
